import type { User } from '@/@types/user'
import JikeLogo from '@/components/Icons/JikeLogo'
import { CONFIRM_SESSION, CREATE_SESSION, USER_PROFILE } from '@/constants/api'
import storage from '@/utils/Storage'
import { getLoginQRCodeURL, refreshTokens } from '@/utils/fetchers/auth'
import { currentUserQueryOptions } from '@/utils/fetchers/user'
import queryClient from '@/utils/queryClient'
import { generalAxiosInstance } from '@/utils/request'
import { css } from '@emotion/react'
import { Button } from '@ruguoapp/jike-fe-components'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { isNil } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { QRCodeCanvas } from 'qrcode.react'
import { type HTMLAttributes, useMemo, useState } from 'react'
import { useTitle } from 'react-use'
import { z } from 'zod'
import ScanQRCodeTutorial from '../components/ScanQRCodeTutorial'

export const Route = createFileRoute('/login')({
	component: LoginPage,
	validateSearch: z.object({
		redirect: z.string().optional(),
	}),
	beforeLoad: async ({ search }) => {
		const user = await queryClient.ensureQueryData<User | null>({
			queryKey: currentUserQueryOptions.queryKey,
			queryFn: async () =>
				generalAxiosInstance
					.get(USER_PROFILE, {
						headers: {
							'x-jike-access-token': storage.auth.token,
						},
					})
					.then((res) => res.data.user as User)
					.catch(() => null),
		})
		const redirectURL = search.redirect && decodeURIComponent(search.redirect)

		if (!isNil(user)) {
			if (redirectURL) {
				const urlObject = new URL(redirectURL)
				throw redirect({
					to: urlObject.pathname,
					replace: true,
				})
			} else {
				throw redirect({ to: '/', replace: true })
			}
		}
	},
})

export interface LoginPageProps extends HTMLAttributes<HTMLDivElement> {}

export default function LoginPage({ ...props }: LoginPageProps) {
	useTitle(`登录 - 即刻`)

	const [isScanCodeTutorialShow, setIsScanCodeTutorialShow] = useState<boolean>(false)

	const { data: uuid } = useSuspenseQuery<string>({
		queryKey: [CREATE_SESSION],
		queryFn: async () =>
			await generalAxiosInstance
				.get(CREATE_SESSION)
				.then((res) => res.data.uuid)
				.then((uuid) => {
					if (!uuid) {
						throw new Error('No valid uuid')
					}
					return uuid
				}),
	})
	const loginQRCodeURL = useMemo(() => getLoginQRCodeURL(uuid), [uuid])

	useQuery({
		queryKey: [CONFIRM_SESSION, uuid],
		queryFn: async () =>
			generalAxiosInstance
				.get(CONFIRM_SESSION, {
					params: {
						uuid,
					},
				})
				.then((res) => res.data)
				.then((data) => {
					const refreshToken = data['x-jike-refresh-token']
					const token = data.token

					if (!data.confirmed) {
						throw new Error('Not Confirmed')
					}
					return {
						token,
						refreshToken,
					}
				})
				.then((tokens) => refreshTokens(tokens.refreshToken))
				.then(async (tokens) => {
					storage.auth.token = tokens.token
					storage.auth.refreshToken = tokens.refreshToken

					await queryClient.invalidateQueries({
						queryKey: currentUserQueryOptions.queryKey,
						refetchType: 'all',
					})

					queryClient.setQueryData([CREATE_SESSION], null)
					window.location.reload()
					return tokens
				}),
		enabled: !!uuid,
		retry: !!uuid,
		retryDelay: 1000,
	})

	return (
		<motion.div
			css={css`
				height: 100vh;
				width: 100vw;
				display: flex;
				justify-content: center;
				align-items: center;
			`}
			initial={{
				opacity: 0,
			}}
			animate={{
				opacity: 1,
				transition: {
					delay: 0.3,
				},
			}}
			exit={{
				opacity: 0,
			}}
		>
			<motion.div
				layout
				css={css`
					min-width: 400px;
					height: 480px;
					background-color: var(--bg-body-base-1);
					box-shadow: 0 4px 32px 0 var(--solid-shadow-1);
					border-radius: 16px;
					padding: 20px;
					display: flex;
				`}
			>
				<motion.div
					layout
					css={css`
						width: 400px;
						display: flex;
						flex-direction: column;
						align-items: center;
						justify-content: center;
					`}
				>
					<div
						css={css`
							flex: 1;
							display: flex;
							justify-content: center;
							align-items: center;
							flex-direction: column;
						`}
					>
						<JikeLogo
							css={css`
								margin-bottom: 32px;
							`}
						/>

						<div
							css={css`
								height: 152px;
								width: 152px;
							`}
						>
							{loginQRCodeURL && (
								<div
									css={css`
										box-shadow: 0 4px 32px 0 var(--solid-shadow-1);
										border-radius: 10px;
										padding: 0.75rem;
										background-color: #fff;
									`}
								>
									<QRCodeCanvas
										value={loginQRCodeURL}
										css={css`
											display: block;
										`}
									/>
								</div>
							)}
						</div>

						<div
							css={css`
								margin-top: 20px;

								strong {
									margin: 0 2px;
								}
							`}
						>
							可使用
							<strong>即刻App</strong>
							扫码
						</div>
					</div>

					<footer>
						<Button
							appearance="text"
							size="sm"
							onClick={() => {
								setIsScanCodeTutorialShow((prev) => !prev)
							}}
						>
							如何扫码
						</Button>
					</footer>
				</motion.div>

				<AnimatePresence mode="popLayout">
					{isScanCodeTutorialShow && <ScanQRCodeTutorial layout />}
				</AnimatePresence>
			</motion.div>
		</motion.div>
	)
}
